import { useState } from "react";
import { Plus, Search, Pencil, Trash2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable, Column } from "@/components/shared/DataTable";
import { Pagination } from "@/components/shared/Pagination";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { useCustomers, useDeleteCustomer } from "@/hooks/useCustomers";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuthStore } from "@/store/auth.store";
import { Customer } from "@/api/customer.api";
import { CustomerFormDialog } from "./CustomerFormDialog";
import { CustomerDetailDialog } from "./CustomerDetailDialog";
import { isAxiosError } from "axios";

const PAGE_SIZE = 10;

export function CustomersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);

  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);

  const role = useAuthStore((s) => s.user?.role);
  const { toast } = useToast();

  const { data, isLoading } = useCustomers({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined });
  const deleteMutation = useDeleteCustomer();

  const columns: Column<Customer>[] = [
    { key: "code", header: "ID", render: (c) => <span className="font-data">{c.customerCode}</span> },
    { key: "name", header: "Name", render: (c) => <span className="font-medium">{c.fullName}</span> },
    { key: "phone", header: "Phone", render: (c) => <span className="font-data">{c.phone}</span> },
    { key: "company", header: "Company", render: (c) => c.company ?? "—" },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (c) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" onClick={() => setViewingCustomer(c)} aria-label="View">
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setEditingCustomer(c);
              setFormOpen(true);
            }}
            aria-label="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {role === "ADMIN" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDeletingCustomer(c)}
              aria-label="Delete"
              className="hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleDelete = async () => {
    if (!deletingCustomer) return;
    try {
      await deleteMutation.mutateAsync(deletingCustomer.id);
      toast({ title: "Customer deleted", variant: "success" });
      setDeletingCustomer(null);
    } catch (err) {
      const message = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      toast({ title: "Could not delete customer", description: message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Customers</h1>
          <p className="text-sm text-muted-foreground">Manage customer records and view their service history.</p>
        </div>
        <Button
          onClick={() => {
            setEditingCustomer(null);
            setFormOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add customer
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name, phone, email, ID..."
          className="pl-9"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          rowKey={(c) => c.id}
          onRowClick={(c) => setViewingCustomer(c)}
          emptyMessage="No customers yet. Add your first customer to get started."
        />
        <Pagination page={page} limit={PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
      </div>

      <CustomerFormDialog open={formOpen} onOpenChange={setFormOpen} customer={editingCustomer} />
      <CustomerDetailDialog
        open={Boolean(viewingCustomer)}
        onOpenChange={(open) => !open && setViewingCustomer(null)}
        customer={viewingCustomer}
      />
      <ConfirmDialog
        open={Boolean(deletingCustomer)}
        onOpenChange={(open) => !open && setDeletingCustomer(null)}
        title="Delete customer?"
        description={`This will remove ${deletingCustomer?.fullName ?? "this customer"} from your records. This can't be undone. Customers with open complaints can't be deleted.`}
        confirmLabel="Delete"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
